import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookOpen, CheckCircle2, Clock, Lock, Megaphone, X } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { gradeText } from "@/lib/gradeLabel";
import { ITEM_LABEL, lockedUntilCopy } from "./itemTypes";
import { Mascot, type MascotPose } from "./Mascot";
import StudentGuardians from "./StudentGuardians";
import type { StudentCourse } from "./MyLearning";
import {
  announcementKey,
  dismissAnnouncementKey,
  dismissAnnouncementKeys,
  loadDismissedAnnouncementKeys,
} from "@/lib/dismissedAnnouncements";
import {
  dismissGradedKey,
  dismissGradedKeys,
  gradedNoticeKey,
  loadDismissedGradedKeys,
} from "@/lib/dismissedGraded";

/**
 * Today — the student home.
 *
 * Structure ported from the original: a course-coloured banner across the top
 * of the hero, the eyebrow and the next thing to do, stat chips, and two
 * stacked actions — continue, or open the whole course. Then due-soon and
 * the course strip. The mascot sits in the hero (no right rail).
 *
 * Locked due rows stay locked. Clicking one opens the earlier step that
 * unlocks it — students cannot jump 1.4 → 1.5. Copy names that step.
 *
 * Teacher updates come from class announcements on enrolled courses.
 * Graded notices are one row per paper a teacher has finished marking.
 * Minutes today are not recorded, so the chips count activities instead.
 */

type Hero = {
  activityId: string;
  courseId: string;
  courseTitle: string;
  ancestors: { type: string; title: string | null }[];
  type: string;
  title: string | null;
  dueAt: string | null;
  pointsPossible: number | null;
  resumed: boolean;
};

type DueItem = {
  activityId: string;
  courseId: string;
  courseTitle: string;
  type: string;
  title: string | null;
  dueAt: string;
  pointsPossible: number | null;
  locked?: boolean;
  /** For a locked item: the openable step in that course to click now. */
  startWith?: { activityId: string; title: string | null } | null;
};

type GradedItem = {
  activityId: string;
  courseId: string;
  courseTitle: string;
  type: string;
  title: string | null;
  pointsEarned: number | null;
  pointsPossible: number | null;
  gradedAt: string;
};

type TodayData = {
  courses: StudentCourse[];
  hero: Hero | null;
  nextByCourse?: Hero[];
  due: DueItem[];
  stats: {
    activitiesToday: number;
    coursesInProgress: number;
    activitiesDone: number;
    activitiesTotal: number;
  };
  announcements: Announcement[];
  schoolAnnouncements?: Announcement[];
  graded?: GradedItem[];
};

type Announcement = {
  id: string;
  courseId: string | null;
  courseTitle: string | null;
  body: string;
  authorName: string | null;
  at: string;
};

/**
 * What the hero button should say. "Start assigned work" is staff vocabulary,
 * and on a reading it is simply wrong — you do not "start assigned work", you
 * read. Name the thing the student is about to do.
 */
function startVerb(type: string) {
  switch (type) {
    case "reading":
      return "Start reading";
    case "video":
      return "Watch the video";
    case "audio":
      return "Listen";
    case "slides":
      return "Open the slides";
    case "key_terms":
      return "Learn the words";
    case "discussion":
      return "Join the discussion";
    case "homework":
      return "Start the homework";
    case "assessment":
    case "exam":
      return "Start";
    default:
      return "Start";
  }
}

function postedLabel(iso: string) {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  const days = Math.floor((Date.now() - then.getTime()) / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return then.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function dueLabel(iso: string) {
  const due = new Date(iso);
  const date = due.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(due.getFullYear() === new Date().getFullYear() ? {} : { year: "numeric" as const }),
  });
  const days = Math.round((due.getTime() - Date.now()) / 86400000);
  if (days < 0) return { text: `Overdue · ${date}`, late: true };
  if (days === 0) return { text: `Due today · ${date}`, late: true };
  if (days === 1) return { text: `Due tomorrow · ${date}`, late: false };
  return { text: date, late: false };
}

/** Pose + a short line. The original sat this on the right rail; here it
 *  lives in the hero so Today still has a guide without a third column. */
function todayGuide(hero: Hero | null, hasCourses: boolean): { pose: MascotPose; line: string } {
  if (!hero) {
    return hasCourses
      ? { pose: "celebrating", line: "You are all caught up." }
      : { pose: "sleeping", line: "Nothing assigned yet." };
  }
  if (hero.type === "reading") return { pose: "reading", line: "Finish this to unlock what comes next." };
  if (hero.type === "video" || hero.type === "slides" || hero.type === "audio") {
    return { pose: "laptop", line: "Finish this to unlock what comes next." };
  }
  if (hero.type === "key_terms") return { pose: "book", line: "Finish this to unlock what comes next." };
  if (hero.resumed) return { pose: "waving", line: "Pick up where you left off." };
  return { pose: "pointing", line: "This is next. Finish it to unlock the rest." };
}

export default function Today({ name }: { name?: string }) {
  const navigate = useNavigate();
  const [data, setData] = useState<TodayData | null>(null);
  const [error, setError] = useState("");
  const [dismissed, setDismissed] = useState(() => loadDismissedAnnouncementKeys());
  const [dismissedGraded, setDismissedGraded] = useState(() => loadDismissedGradedKeys());

  useEffect(() => {
    let cancelled = false;
    api<{ success: boolean; data: TodayData }>("/api/student/today")
      .then((env) => {
        if (!cancelled) setData(env.data);
      })
      .catch((err) => {
        if (!cancelled) setError(errorMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const firstName = (name || "").split(" ")[0];

  if (error) {
    return (
      <div className="student-page">
        <div className="student-inline-error" role="alert">
          {error}
        </div>
      </div>
    );
  }

  if (!data) {
    return <TodayOpeningSkeleton />;
  }

  const { hero, due, courses, stats, announcements = [], schoolAnnouncements = [], nextByCourse = [], graded = [] } = data;
  const guide = todayGuide(hero, courses.length > 0);
  const visibleAnnouncements = announcements.filter((row) => !dismissed.has(announcementKey(row)));
  const visibleSchoolAnnouncements = schoolAnnouncements.filter((row) => !dismissed.has(announcementKey(row)));
  const visibleGraded = graded.filter((row) => !dismissedGraded.has(gradedNoticeKey(row)));
  const heroCourse = hero ? courses.find((c) => c.id === hero.courseId) : null;
  const otherNext = nextByCourse.filter((row) => !hero || row.courseId !== hero.courseId);

  return (
    <div className="student-page">
      {hero ? (
        <section className="student-hero">
          <div className="student-hero__banner">{hero.courseTitle}</div>
          <div className="student-hero__body">
            <div className="student-hero__copy">
              <span className="student-eyebrow">Today</span>
              <h1>{hero.title || "Untitled activity"}</h1>
              {hero.ancestors.some((a) => a.title) && (
                <p className="student-hero__sub">
                  {hero.ancestors.map((a) => a.title).filter(Boolean).join(" · ")}
                </p>
              )}
              <div className="student-chips">
                <span className="student-chip student-chip--accent">
                  {ITEM_LABEL[hero.type] || hero.type}
                </span>
                <span className="student-chip">
                  <CheckCircle2 size={13} aria-hidden />
                  {stats.activitiesToday} today
                </span>
                {heroCourse?.hasPublishedWork ? (
                  <span className="student-chip">
                    <BookOpen size={13} aria-hidden />
                    {heroCourse.activitiesDone} of {heroCourse.activitiesTotal} in this class
                  </span>
                ) : null}
                {hero.pointsPossible ? (
                  <span className="student-chip">{hero.pointsPossible} points</span>
                ) : null}
              </div>
            </div>
            <div className="student-hero__guide">
              <Mascot pose={guide.pose} size={88} />
              <p className="student-hero__guide-copy">{guide.line}</p>
            </div>
            <div className="student-hero__actions">
              <button
                type="button"
                className="student-btn"
                onClick={() => navigate(`/student/activity/${hero.activityId}`)}
              >
                {hero.resumed ? "Keep going" : startVerb(hero.type)}
              </button>
              <button
                type="button"
                className="student-btn student-btn--ghost"
                onClick={() => navigate(`/student/course/${hero.courseId}`)}
              >
                Open course
              </button>
            </div>
          </div>
        </section>
      ) : (
        <>
          <div className="student-page-head">
            <span className="student-eyebrow">Today</span>
            <h1>{firstName ? `Welcome back, ${firstName}` : "Welcome back"}</h1>
          </div>
          <div className="student-empty">
            <div>
              <Mascot pose={guide.pose} size={92} />
              <strong>
                {courses.length ? "You are all caught up" : "Nothing assigned yet"}
              </strong>
              <p>
                {courses.length
                  ? "You have finished everything in your courses. New work will appear here."
                  : "Your courses will appear here once your school enrolls you in them."}
              </p>
            </div>
          </div>
        </>
      )}

      {otherNext.length > 0 && (
        <div className="student-card" style={{ marginTop: 15 }}>
          <div className="student-section__head" style={{ marginBottom: 8 }}>
            <span className="student-eyebrow">Your other classes</span>
          </div>
          {otherNext.map((item) => (
            <button
              key={item.courseId}
              type="button"
              className="student-row"
              style={{ width: "100%", background: "none", border: 0, cursor: "pointer" }}
              onClick={() => navigate(`/student/activity/${item.activityId}`)}
            >
              <span style={{ textAlign: "left", minWidth: 0 }}>
                <strong>{item.title || "Untitled activity"}</strong>
                <span className="student-row__sub">
                  {item.courseTitle} · {ITEM_LABEL[item.type] || item.type}
                  {item.resumed ? " · pick up where you left off" : ""}
                </span>
              </span>
              <span className="student-row__due">{item.resumed ? "Keep going" : "Next"}</span>
            </button>
          ))}
        </div>
      )}

      {due.length > 0 && (
        <div className="student-card" style={{ marginTop: 15 }}>
          <div className="student-section__head" style={{ marginBottom: 8 }}>
            <span className="student-eyebrow">Due soon</span>
          </div>
          {due.map((item) => {
            const label = dueLabel(item.dueAt);
            return (
              <button
                key={item.activityId}
                type="button"
                className="student-row"
                style={{
                  width: "100%",
                  background: "none",
                  border: 0,
                  cursor: "pointer",
                  opacity: item.locked ? 0.7 : 1,
                }}
                onClick={() => {
                  // A locked row still goes somewhere useful: the step that
                  // gets the student towards it, not a dead "locked" wall.
                  if (!item.locked) return navigate(`/student/activity/${item.activityId}`);
                  if (item.startWith) return navigate(`/student/activity/${item.startWith.activityId}`);
                  return navigate(`/student/course/${item.courseId}`);
                }}
              >
                <span style={{ textAlign: "left", minWidth: 0 }}>
                  <strong>{item.title || "Untitled activity"}</strong>
                  <span className="student-row__sub">
                    {item.courseTitle}
                    {item.pointsPossible
                      ? ` · ${item.pointsPossible} ${item.pointsPossible === 1 ? "point" : "points"}`
                      : ""}
                    {item.locked
                      ? ` · ${lockedUntilCopy(item.startWith?.title)}`
                      : ""}
                  </span>
                </span>
                <span className={`student-row__due${label.late ? " is-overdue" : ""}`}>
                  {item.locked ? <Lock size={12} aria-hidden /> : <Clock size={12} aria-hidden />}
                  {label.text}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {visibleGraded.length > 0 && (
        <div className="student-card" style={{ marginTop: 15 }}>
          <div className="student-section__head" style={{ marginBottom: 8 }}>
            <span className="student-eyebrow">
              <CheckCircle2 size={12} aria-hidden /> Graded
            </span>
            {visibleGraded.length > 1 ? (
              <button
                type="button"
                className="student-text-link"
                onClick={() =>
                  setDismissedGraded(dismissGradedKeys(visibleGraded.map(gradedNoticeKey)))
                }
              >
                Clear all
              </button>
            ) : null}
          </div>
          {visibleGraded.map((item) => (
            <div key={gradedNoticeKey(item)} className="student-announcement">
              <button
                type="button"
                className="student-graded-open"
                onClick={() => {
                  setDismissedGraded(dismissGradedKey(gradedNoticeKey(item)));
                  navigate(`/student/grades/${item.courseId}`);
                }}
              >
                <strong>{item.title || "Untitled activity"} graded</strong>
                <span className="student-row__sub">
                  {[item.courseTitle, postedLabel(item.gradedAt)].filter(Boolean).join(" · ")}
                </span>
              </button>
              <button
                type="button"
                className="student-announcement__dismiss"
                aria-label="Dismiss graded notice"
                onClick={() => setDismissedGraded(dismissGradedKey(gradedNoticeKey(item)))}
              >
                <X size={14} aria-hidden />
              </button>
            </div>
          ))}
        </div>
      )}

      {visibleSchoolAnnouncements.length > 0 && (
        <div className="student-card" style={{ marginTop: 15 }}>
          <div className="student-section__head" style={{ marginBottom: 8 }}>
            <span className="student-eyebrow">
              <Megaphone size={12} aria-hidden /> School announcement
            </span>
            {visibleSchoolAnnouncements.length > 1 ? (
              <button
                type="button"
                className="student-text-link"
                onClick={() =>
                  setDismissed(dismissAnnouncementKeys(visibleSchoolAnnouncements.map(announcementKey)))
                }
              >
                Clear all
              </button>
            ) : null}
          </div>
          {visibleSchoolAnnouncements.map((item) => (
            <div key={item.id} className="student-announcement">
              <div>
                <p>{item.body}</p>
                <span className="student-row__sub">
                  {[item.authorName, postedLabel(item.at)].filter(Boolean).join(" · ")}
                </span>
              </div>
              <button
                type="button"
                className="student-announcement__dismiss"
                aria-label="Dismiss announcement"
                onClick={() => setDismissed(dismissAnnouncementKey(announcementKey(item)))}
              >
                <X size={14} aria-hidden />
              </button>
            </div>
          ))}
        </div>
      )}

      {visibleAnnouncements.length > 0 && (
        <div className="student-card" style={{ marginTop: 15 }}>
          <div className="student-section__head" style={{ marginBottom: 8 }}>
            <span className="student-eyebrow">
              <Megaphone size={12} aria-hidden /> Teacher updates
            </span>
            {visibleAnnouncements.length > 1 ? (
              <button
                type="button"
                className="student-text-link"
                onClick={() =>
                  setDismissed(dismissAnnouncementKeys(visibleAnnouncements.map(announcementKey)))
                }
              >
                Clear all
              </button>
            ) : null}
          </div>
          {visibleAnnouncements.map((item) => (
            <div key={item.id} className="student-announcement">
              <div>
                <p>{item.body}</p>
                <span className="student-row__sub">
                  {[item.authorName, item.courseTitle, postedLabel(item.at)].filter(Boolean).join(" · ")}
                </span>
              </div>
              <button
                type="button"
                className="student-announcement__dismiss"
                aria-label="Dismiss announcement"
                onClick={() => setDismissed(dismissAnnouncementKey(announcementKey(item)))}
              >
                <X size={14} aria-hidden />
              </button>
            </div>
          ))}
        </div>
      )}

      {courses.length > 0 && (
        <section className="student-section">
          <div className="student-section__head">
            <div>
              <span className="student-eyebrow">Courses</span>
              <h2>
                {stats.coursesInProgress || courses.length}{" "}
                {(stats.coursesInProgress || courses.length) === 1 ? "course" : "courses"} in
                progress
              </h2>
            </div>
            <Link className="student-text-link" to="/student/learning">
              My Learning →
            </Link>
          </div>
          <div className="student-course-strip">
            {courses.map((course) => (
              <button
                key={course.id}
                type="button"
                className="student-course-card"
                onClick={() => navigate(`/student/course/${course.id}`)}
              >
                <div className="student-course-card__top">
                  <span>Course</span>
                  {course.grade?.percent != null ? (
                    <span title="Your grade on marked work">{gradeText(course.grade)}</span>
                  ) : course.hasPublishedWork ? (
                    <span className="student-course-card__nomark">No marks yet</span>
                  ) : null}
                </div>
                <h3>{course.title}</h3>
                <p>
                  {course.hasPublishedWork
                    ? `${course.activitiesDone} of ${course.activitiesTotal} activities`
                    : "Nothing to do yet"}
                </p>
                {course.hasPublishedWork && (
                  <div className="student-meter">
                    <i style={{ width: `${course.percent}%` }} />
                  </div>
                )}
              </button>
            ))}
          </div>
        </section>
      )}
      <StudentGuardians />
    </div>
  );
}

function TodayOpeningSkeleton() {
  return (
    <div className="student-page" aria-busy="true" aria-live="polite">
      <span className="student-vh">Loading your day</span>
      <section className="student-hero">
        <div className="student-ghost student-ghost--banner" />
        <div className="student-hero__body">
          <div className="student-hero__copy">
            <div className="student-ghost student-ghost--chip" />
            <div className="student-ghost student-ghost--title" />
            <div className="student-ghost student-ghost--line student-ghost--w60" style={{ marginTop: 10 }} />
            <div className="student-ghost-row">
              <div className="student-ghost student-ghost--chip" />
              <div className="student-ghost student-ghost--chip" />
              <div className="student-ghost student-ghost--chip" />
            </div>
          </div>
          <div className="student-ghost student-ghost--mascot" aria-hidden />
          <div className="student-hero__actions">
            <div className="student-ghost student-ghost--choice" />
            <div className="student-ghost student-ghost--choice" />
          </div>
        </div>
      </section>
      <div className="student-ghost-card">
        <div className="student-ghost student-ghost--chip" />
        <div className="student-ghost student-ghost--line student-ghost--w80" />
        <div className="student-ghost student-ghost--meta" />
        <div className="student-ghost student-ghost--line student-ghost--w90" />
        <div className="student-ghost student-ghost--meta" />
      </div>
      <section className="student-section">
        <div className="student-section__head">
          <div>
            <div className="student-ghost student-ghost--chip" />
            <div className="student-ghost student-ghost--title" />
          </div>
        </div>
        <div className="student-course-strip">
          <GhostCourseCard />
          <GhostCourseCard />
          <GhostCourseCard />
        </div>
      </section>
    </div>
  );
}

function GhostCourseCard() {
  return (
    <div className="student-ghost-course" aria-hidden>
      <div className="student-ghost student-ghost--chip" />
      <div className="student-ghost student-ghost--line student-ghost--w80" />
      <div className="student-ghost student-ghost--meta" />
      <div className="student-ghost student-ghost--meter" />
    </div>
  );
}
